import { PagedResponse } from '@api/Trinium/types';
import { PopupContentProps } from '@forms/Components/Dropdowns/ButtonDropdown';
import useDebouncer from '@hooks/useDebounce';
import { UIEvent, useCallback, useEffect, useRef, useState } from 'react';
import { InputAdornment, LinearProgress, List, ListItem, ListItemButton, ListItemText, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchTwoTone';

import styles from './ButtonDropdown.module.css';

/** How close to the bottom of the list (in px) the user must scroll before the next page is requested */
const LOAD_MORE_THRESHOLD_PX = 40;

interface SearchTextButtonPopupContent<T> extends PopupContentProps {
	defaultItems?: T[];
	/** Fetches one page (1-based) of results for the search text. A source that is not paged should return hasMore false. */
	onSearchCallback: (searchInput: string, page: number) => Promise<PagedResponse<T>>;
	getOptionText: (option: T) => string;
	onError?: (error: unknown) => void;
	minSearchLen?: number;
	debounceTimeMs?: number;
}

/**
 * Generic Popup component that renders with a text box input above the list where the users
 * input triggers a callback to populate the list through an API filter. Scrolling to the bottom
 * of the list requests the next page while the callback reports more results exist.
 */
export default function SearchablePopupContent<T>({
	getOptionText,
	onChange,
	onSearchCallback,
	onError,
	minSearchLen,
	defaultItems,
	...props
}: SearchTextButtonPopupContent<T>) {
	const [searchInput, setSearchInput] = useState('');
	const searchInputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const [items, setItems] = useState<T[] | null>(defaultItems ?? null);
	const [hasMore, setHasMore] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	/** The search text and page the items list currently reflects, so "load more" continues the right search even if the input has since changed */
	const loadedRef = useRef({ searchInput: '', page: 1 });
	/** Bumped per request so a response from a superseded search cannot land in the list */
	const requestIdRef = useRef(0);

	const fetchPage = useCallback(
		(searchText: string, page: number) => {
			const requestId = ++requestIdRef.current;
			setIsLoading(true);
			setErrorMessage(null);
			onSearchCallback(searchText, page)
				.then((response) => {
					if (requestId !== requestIdRef.current) return;
					loadedRef.current = { searchInput: searchText, page };
					// A fresh search replaces the list; if the old list was scrolled to its end the shorter new list would
					// still sit at the bottom and immediately trigger a load of page 2.
					if (page === 1 && listRef.current) listRef.current.scrollTop = 0;
					setHasMore(response.hasMore);
					setItems((current) => (page === 1 || !current ? response.items : [...current, ...response.items]));
				})
				.catch((exception: unknown) => {
					if (requestId !== requestIdRef.current) return;
					// A failed first page clears the previous search's items so they are not mistaken for this search's results;
					// a failed later page keeps what is loaded and the user can scroll again to retry.
					if (page === 1) {
						setItems(null);
						setHasMore(false);
					}
					setErrorMessage(exception instanceof Error && exception.message ? exception.message : 'Unknown error');
					if (onError) onError(exception);
				})
				.finally(() => {
					if (requestId === requestIdRef.current) setIsLoading(false);
				});
		},
		[onSearchCallback, onError]
	);

	const fetchOnSearch = useCallback(() => {
		if (searchInput.length > (minSearchLen ?? 2)) {
			fetchPage(searchInput, 1);
			return;
		}
		requestIdRef.current++;
		setIsLoading(false);
		setHasMore(false);
		setErrorMessage(null);
		if (defaultItems) setItems(defaultItems);
	}, [searchInput, fetchPage, minSearchLen, defaultItems]);

	const onListScroll = (event: UIEvent<HTMLUListElement>) => {
		if (!hasMore || isLoading) return;
		const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
		if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX) {
			fetchPage(loadedRef.current.searchInput, loadedRef.current.page + 1);
		}
	};

	/** On Render, focus the search input */
	useEffect(() => {
		searchInputRef.current?.focus();
	}, []);

	useDebouncer(fetchOnSearch, [searchInput], props.debounceTimeMs ?? 1000);

	return (
		<div className={styles['popup-list-container']}>
			<TextField
				fullWidth
				placeholder='Start Typing to Search...'
				value={searchInput}
				onChange={(event) => {
					setSearchInput(event.target.value);
				}}
				variant='standard'
				slotProps={{
					input: {
						startAdornment: (
							<InputAdornment
								position='start'
								className={styles['search-ico']}
							>
								<SearchIcon fontSize='small' />
							</InputAdornment>
						)
					}
				}}
				inputRef={searchInputRef}
			/>
			<LinearProgress className={!isLoading ? styles.hidden : undefined} />
			<List
				disablePadding
				dense
				className={styles['popup-list']}
				ref={listRef}
				onScroll={onListScroll}
			>
				{!items && !errorMessage && (
					<ListItem>
						<ListItemText
							primary='Enter at least 3 characters to begin search'
							className={styles.secondary}
						/>
					</ListItem>
				)}
				{items && items.length === 0 && !errorMessage ? (
					<ListItem>
						<ListItemText
							primary='No Results'
							className={styles.secondary}
						/>
					</ListItem>
				) : (
					items?.map((item, index) => (
						<ListItemButton
							key={index}
							onClick={() => {
								onChange(item);
							}}
							className={styles['list-item']}
						>
							<ListItemText primary={getOptionText(item)} />
						</ListItemButton>
					))
				)}
				{errorMessage && (
					<ListItem>
						<ListItemText
							primary='Search failed'
							secondary={errorMessage}
							className={styles['search-error']}
						/>
					</ListItem>
				)}
			</List>
		</div>
	);
}
